import { useCallback, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Archive, Bell, Inbox, KeyRound, Link2, Pencil } from "lucide-react";
import { ApiError } from "@/api/client";
import type { PartyDetailDto, PartyType } from "@/types/parties";
import { PARTY_PERMISSIONS, partyAccountType, partyBasePath } from "@/types/parties";
import {
  Badge,
  Button,
  DetailField as Field,
  DetailFieldList,
  EmptyState,
  RECORD_CARD_MIN_WIDTH,
  ResponsiveGrid,
  StatusBadge,
} from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import {
  CROSS_CUTTING_TAB_ICONS,
  EntityTabBar,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";
import { PortalAccountTab } from "@/components/portalAbsorption/PortalAccountTab";
import { AccountNotificationsPanel } from "@/components/portalAbsorption/AccountNotificationsPanel";
import { AccountReviewRequestsTab } from "@/components/portalAbsorption/AccountReviewRequestsTab";
import { DocumentPanel } from "@/pages/documents/components/DocumentPanel";
import { TaskListMini } from "@/pages/tasks/components/TaskListMini";
import { AppointmentListMini } from "@/pages/calendar/components/AppointmentListMini";
import { RelatedConversationsPanel } from "@/components/messaging/RelatedConversationsPanel";
import { CommunicationsTabPanel } from "@/pages/communications/components/CommunicationsTabPanel";
import { NotesTabPanel } from "@/pages/notes/components/NotesTabPanel";
import { ActivityTabPanel } from "@/pages/activity/components/ActivityTabPanel";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import { useToast } from "@/components/ui/Toast";
import { useDateFormatter } from "@/utils/datetime";
import { displayEmail, NO_EMAIL_ON_FILE } from "@/design-system/lib/format";
import {
  useArchiveParty,
  usePartyDetail,
  useSendPartyPortalInvite,
} from "@/hooks/parties/useParties";
import { PartyContactsCard } from "./components/PartyContactsCard";
import { OwnedEntitiesCard } from "./components/OwnedEntitiesCard";

// Borrower/Seller party detail — the §2.1 tab standard: Overview · Portal
// Account · Review Queue + Requests · Notifications · Linked, then the seven
// cross-cutting tabs ALWAYS last in the standard order. The portal-account
// card moved OFF Overview into its own Portal Account tab; every action on
// this page stays in place (popups/slide-outs — never leave the entity).

type TabId =
  | "overview"
  | "portal-account"
  | "review-requests"
  | "notifications"
  | "linked"
  | "documents"
  | "tasks"
  | "calendar"
  | "messages"
  | "communications"
  | "notes"
  | "activity";

const TABS: readonly TabId[] = [
  "overview",
  "portal-account",
  "review-requests",
  "notifications",
  "linked",
  "documents",
  "tasks",
  "calendar",
  "messages",
  "communications",
  "notes",
  "activity",
] as const;

function isTabId(value: string | null): value is TabId {
  return value !== null && (TABS as readonly string[]).includes(value);
}

const TAB_ITEMS: ReadonlyArray<EntityTabItem<TabId>> = [
  { key: "overview", label: "Overview", icon: CROSS_CUTTING_TAB_ICONS.overview },
  { key: "portal-account", label: "Portal account", icon: KeyRound },
  { key: "review-requests", label: "Review queue & requests", icon: Inbox },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "linked", label: "Linked deals & loans", icon: Link2 },
  { key: "documents", label: "Documents", icon: CROSS_CUTTING_TAB_ICONS.documents },
  { key: "tasks", label: "Tasks", icon: CROSS_CUTTING_TAB_ICONS.tasks },
  { key: "calendar", label: "Calendar", icon: CROSS_CUTTING_TAB_ICONS.calendar },
  { key: "messages", label: "Messages", icon: CROSS_CUTTING_TAB_ICONS.messages },
  { key: "communications", label: "Communications", icon: CROSS_CUTTING_TAB_ICONS.communications },
  { key: "notes", label: "Notes", icon: CROSS_CUTTING_TAB_ICONS.notes },
  { key: "activity", label: "Activity", icon: CROSS_CUTTING_TAB_ICONS.activity },
] as const;

export function PartyDetailPage({ type }: { type: PartyType }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const dialogs = useDialogs();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const { date } = useDateFormatter();

  const perms = PARTY_PERMISSIONS[type];
  const base = partyBasePath(type);
  const title = type === "Borrower" ? "Borrowers" : "Sellers";

  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: TabId = isTabId(tabParam) ? tabParam : "overview";
  const setTab = useCallback(
    (next: TabId) => {
      const updated = new URLSearchParams(params);
      if (next === "overview") updated.delete("tab");
      else updated.set("tab", next);
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  const [archiveBlocked, setArchiveBlocked] = useState<string | null>(null);

  const canUpdate = hasPermission(perms.Update);
  const canDelete = hasPermission(perms.Delete);

  const detailQuery = usePartyDetail(type, id);
  const archive = useArchiveParty(type);
  const sendInvite = useSendPartyPortalInvite();

  useBreadcrumbs(
    detailQuery.data
      ? [{ label: title, href: base }, { label: detailQuery.data.displayName }]
      : null,
  );

  if (detailQuery.isLoading) {
    return <div className="text-caption uppercase tracking-wide text-gray-mid">Loading…</div>;
  }
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className="text-small text-status-error">
        {(detailQuery.error as Error)?.message ?? `${type} not found.`}
      </div>
    );
  }

  const party = detailQuery.data;

  const onArchive = async () => {
    const ok = await dialogs.confirm({
      title: `Archive ${party.displayName}?`,
      message:
        "This party will no longer appear in active lists. Existing deal and loan references are preserved.",
      confirmLabel: "Archive",
      destructive: true,
    });
    if (!ok) return;
    archive.mutate(party.id, {
      onSuccess: () => {
        toast.success(`${type} archived.`);
        navigate(base);
      },
      onError: (e) => {
        if (e instanceof ApiError && e.status === 409) setArchiveBlocked(e.message);
        else toast.error(e instanceof Error ? e.message : "Archive failed.");
      },
    });
  };

  return (
    <>
      <PageHeader
        eyebrow={`Parties · ${type}`}
        title={party.displayName}
        actions={
          <>
            {canUpdate && (
              <Button size="sm" variant="secondary" onClick={() => navigate(`${base}/${party.id}/edit`)}>
                <Pencil size={14} aria-hidden /> Edit
              </Button>
            )}
            {canDelete && (
              <Button size="sm" variant="destructive" onClick={() => void onArchive()} disabled={archive.isPending}>
                <Archive size={14} aria-hidden /> Archive
              </Button>
            )}
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-space-3 -mt-space-3 mb-space-4">
        <Badge status={party.partyKind === "Business" ? "review" : "draft"}>{party.partyKind}</Badge>
        <Badge status="gold">{type}</Badge>
        <span className="text-caption text-gray-mid min-w-0 break-all" title={party.email}>
          {displayEmail(party.email) ?? NO_EMAIL_ON_FILE}
          {party.phone ? ` · ${party.phone}` : ""}
        </span>
        {party.emailOutOfSyncWithAccount && (
          <StatusBadge tone="warn">Portal login email differs</StatusBadge>
        )}
      </div>

      <EntityTabBar
        ariaLabel={`${type} sections`}
        testIdPrefix="party"
        items={TAB_ITEMS.map((it) => ({ ...it }))}
        value={tab}
        onChange={setTab}
      />

      <div className="pt-space-4">
        {tab === "overview" && (
          <div className="flex flex-col gap-space-4">
            <PartyIdentityCards party={party} />
            <PartyContactsCard
              type={type}
              partyId={party.id}
              contacts={party.contacts}
              canEdit={canUpdate}
            />
            <OwnedEntitiesCard
              type={type}
              partyId={party.id}
              ownedEntities={party.ownedEntities}
              canEdit={canUpdate}
            />
          </div>
        )}

        {tab === "portal-account" && (
          <PortalAccountTab
            portalUserId={party.portalUserId}
            emptyHint="No portal account is linked to this party yet. Accounts are minted automatically when a party is created."
            invite={
              canUpdate
                ? {
                    send: () =>
                      sendInvite.mutate(
                        {
                          email: party.email,
                          fullName: party.displayName,
                          accountType: partyAccountType(type),
                        },
                        {
                          onSuccess: (r) =>
                            toast.success(
                              r.isExistingAccount
                                ? "Invitation re-sent to the existing account."
                                : "Portal invitation sent.",
                            ),
                          onError: (e) =>
                            toast.error(e instanceof Error ? e.message : "Invite failed."),
                        },
                      ),
                    pending: sendInvite.isPending,
                  }
                : undefined
            }
          />
        )}

        {tab === "review-requests" &&
          (party.portalUserId ? (
            <AccountReviewRequestsTab
              scope={{ portalUserId: party.portalUserId }}
              requestComposer={{
                accountType: partyAccountType(type),
                email: party.email,
                // C1 — the party's own record is the created package's owner
                // (the review-time filing default).
                owner: { ownerEntityType: type, ownerEntityId: party.id },
              }}
              emptyMessage="Nothing has been submitted for review by this party yet."
            />
          ) : (
            <EmptyState
              title="No portal account"
              description="Review queue and document requests appear once this party has a linked portal account."
            />
          ))}

        {tab === "notifications" &&
          (party.portalUserId ? (
            <AccountNotificationsPanel portalUserId={party.portalUserId} />
          ) : (
            <EmptyState
              title="No portal account"
              description="Notifications appear once this party has a linked portal account."
            />
          ))}

        {tab === "linked" && <LinkedRecords party={party} />}

        {tab === "documents" && (
          <DocumentPanel ownerType={type} ownerId={party.id} rootName={party.displayName} />
        )}
        {tab === "tasks" && (
          <TaskListMini relatedEntityType={type} relatedEntityId={party.id} />
        )}
        {tab === "calendar" && (
          <AppointmentListMini
            relatedEntityType={type}
            relatedEntityId={party.id}
            entityLabel={party.displayName}
          />
        )}
        {tab === "messages" && (
          <RelatedConversationsPanel
            resourceType={type === "Borrower" ? "borrower" : "seller"}
            resourceId={party.id}
            entityLabel={party.displayName}
          />
        )}
        {tab === "communications" && (
          <CommunicationsTabPanel
            ownerType={type === "Borrower" ? "borrower" : "seller"}
            ownerId={party.id}
            ownerLabel={party.displayName}
          />
        )}
        {tab === "notes" && (
          <NotesTabPanel ownerType={type === "Borrower" ? "borrower" : "seller"} ownerId={party.id} />
        )}
        {tab === "activity" && (
          <ActivityTabPanel ownerType={type === "Borrower" ? "borrower" : "seller"} ownerId={party.id} />
        )}
      </div>

      <div className="mt-space-4 text-caption text-gray-mid">
        Created {date(party.createdAt)}
        {party.modifiedAt ? ` · Modified ${date(party.modifiedAt)}` : ""}
      </div>

      {archiveBlocked && (
        <div
          className="mt-space-3 bg-status-error/10 border-l-[3px] border-status-error text-small text-status-error p-space-3 rounded-md"
          role="alert"
        >
          {archiveBlocked}
        </div>
      )}
    </>
  );
}

function PartyIdentityCards({ party }: { party: PartyDetailDto }) {
  return (
    <ResponsiveGrid cols={2} minColWidth={RECORD_CARD_MIN_WIDTH} gap="md">
      <Section title="Identity">
        {party.partyKind === "Person" ? (
          <>
            <DetailFieldList>
              <Field label="Full name">{party.displayName}</Field>
              <Field label="Date of birth">{party.dateOfBirth ?? "—"}</Field>
              <Field label="SSN">
                <span className="font-mono">{party.ssnMasked ?? "—"}</span>
              </Field>
              <Field label="US citizenship">{party.usCitizen ?? "—"}</Field>
              <Field label="Marital status">{party.maritalStatus ?? "—"}</Field>
            </DetailFieldList>
          </>
        ) : (
          <>
            <DetailFieldList>
              <Field label="Legal name">{party.legalName ?? "—"}</Field>
              <Field label="DBA">{party.dba ?? "—"}</Field>
              <Field label="Entity type">{party.entityType ?? "—"}</Field>
              <Field label="State of formation">{party.stateOfFormation ?? "—"}</Field>
              <Field label="Date of formation">{party.dateOfFormation ?? "—"}</Field>
              <Field label="EIN">
                <span className="font-mono">{party.einMasked ?? "—"}</span>
              </Field>
              <Field label="Formation status">{party.formationStatus ?? "—"}</Field>
            </DetailFieldList>
          </>
        )}
      </Section>

      <Section title="Contact">
        <DetailFieldList>
          <Field label="Email">
            {displayEmail(party.email) ? (
              <span className="break-all" title={party.email}>
                {displayEmail(party.email)}
              </span>
            ) : (
              <span className="text-gray-mid">{NO_EMAIL_ON_FILE}</span>
            )}
          </Field>
          <Field label="Secondary email">{party.secondaryEmail ?? "—"}</Field>
          <Field label="Phone">
            {party.phone || "—"}
            {party.primaryPhoneType ? ` (${party.primaryPhoneType})` : ""}
          </Field>
          <Field label="Secondary phone">{party.secondaryPhone ?? "—"}</Field>
          <Field label="Preferred contact">{party.preferredContactMethod ?? "—"}</Field>
        </DetailFieldList>
      </Section>

      <Section title="Address">
        <AddressBlock label="Physical" address={party.address} />
        {party.mailingAddress && (
          <div className="mt-space-3">
            <AddressBlock label="Mailing" address={party.mailingAddress} />
          </div>
        )}
      </Section>

      <Section title="Banking">
        <DetailFieldList>
          <Field label="ACH name">{party.bankACHName ?? "—"}</Field>
          <Field label="Routing #">
            <span className="font-mono">{party.bankRoutingNumberMasked ?? "—"}</span>
          </Field>
          <Field label="Account #">
            <span className="font-mono">{party.bankAccountNumberMasked ?? "—"}</span>
          </Field>
          <Field label="Account type">{party.bankAccountType ?? "—"}</Field>
        </DetailFieldList>
      </Section>

      {party.partyType === "Borrower" ? (
        <Section title="Borrower preliminaries">
          <DetailFieldList>
            <Field label="Industry experience (yrs)">{party.industryExperienceYears ?? "—"}</Field>
            <Field label="Estimated net worth">{formatMoney(party.estimatedNetWorth)}</Field>
            <Field label="Estimated liquidity">{formatMoney(party.estimatedLiquidity)}</Field>
            <Field label="Preliminary credit score">
              {party.preliminaryCreditScore ?? "—"}
              {party.preliminaryCreditScoreSource ? ` (${party.preliminaryCreditScoreSource})` : ""}
            </Field>
            <Field label="Equity injection source">{party.equityInjectionSource ?? "—"}</Field>
          </DetailFieldList>
        </Section>
      ) : (
        <Section title="Seller preliminaries">
          <DetailFieldList>
            <Field label="Accredited investor">
              {party.accreditedInvestorStatus ?? "—"}
              {party.accreditedInvestorBasis ? ` (${party.accreditedInvestorBasis})` : ""}
            </Field>
            <Field label="Tax filing status">{party.taxFilingStatus ?? "—"}</Field>
            <Field label="Original sale date">{party.originalSaleDate ?? "—"}</Field>
            <Field label="Original sale basis">{party.originalSaleBasisInfo ?? "—"}</Field>
          </DetailFieldList>
        </Section>
      )}

      <Section title="Notes & tags">
        <DetailFieldList>
          <Field label="Notes">{party.generalNotes ?? "—"}</Field>
        </DetailFieldList>
        <div className="flex flex-wrap gap-space-1">
          {party.tags.length === 0 ? (
            <span className="text-caption text-gray-mid">No tags</span>
          ) : (
            party.tags.map((t) => (
              <Badge key={t} status="gold">
                {t}
              </Badge>
            ))
          )}
        </div>
      </Section>
    </ResponsiveGrid>
  );
}

function LinkedRecords({ party }: { party: PartyDetailDto }) {
  return (
    <ResponsiveGrid cols={2} minColWidth={RECORD_CARD_MIN_WIDTH} gap="md">
      <Section title="Deals">
        {party.linkedDeals.length === 0 ? (
          <span className="text-caption text-gray-mid">No linked deals.</span>
        ) : (
          <ul className="m-0 p-0 list-none flex flex-col gap-space-2">
            {party.linkedDeals.map((d) => (
              <li key={d.id} className="flex items-center gap-space-2">
                <Link
                  to={`/deal-flow/deals/${d.id}`}
                  className="font-semibold text-gold underline-offset-2 hover:underline"
                >
                  {d.number}
                </Link>
                <span className="text-caption text-gray-mid">as {d.role}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="Loans">
        {party.linkedLoans.length === 0 ? (
          <span className="text-caption text-gray-mid">No linked loans.</span>
        ) : (
          <ul className="m-0 p-0 list-none flex flex-col gap-space-2">
            {party.linkedLoans.map((l) => (
              <li key={l.id} className="flex items-center gap-space-2">
                <Link
                  to={`/portfolio-admin/loans/${l.id}`}
                  className="font-semibold text-gold underline-offset-2 hover:underline"
                >
                  {l.number}
                </Link>
                <span className="text-caption text-gray-mid">as {l.role}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </ResponsiveGrid>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white-pure border border-border-light rounded-lg p-space-4">
      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mb-space-3 m-0">
        {title}
      </h3>
      <div className="grid grid-cols-1 gap-space-2">{children}</div>
    </section>
  );
}

function AddressBlock({
  label,
  address,
}: {
  label: string;
  address: {
    streetAddress1: string;
    streetAddress2?: string | null;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
}) {
  return (
    <div className="text-small">
      <div className="text-caption uppercase tracking-wide text-gray-mid mb-space-1">{label}</div>
      <div className="text-onyx">{address.streetAddress1 || "—"}</div>
      {address.streetAddress2 && <div className="text-onyx">{address.streetAddress2}</div>}
      <div className="text-onyx">
        {[address.city, address.state].filter(Boolean).join(", ")} {address.zip}
      </div>
      <div className="text-gray-mid">{address.country}</div>
    </div>
  );
}

function formatMoney(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

export function BorrowerDetailPage() {
  return <PartyDetailPage type="Borrower" />;
}

export function SellerDetailPage() {
  return <PartyDetailPage type="Seller" />;
}
